import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

// Locale bundles are loaded on demand — statically importing all six inlined
// ~1.75MB of JSON into the main bundle (≈60% of it), all parsed on the main
// thread before first paint. Only the active language (plus 'en' as fallback
// when it differs) is ever needed for a given merchant session.
const localeLoaders = {
  de: () => import(/* webpackChunkName: "locale-de" */ './de/default.json'),
  en: () => import(/* webpackChunkName: "locale-en" */ './en/default.json'),
  es: () => import(/* webpackChunkName: "locale-es" */ './es/default.json'),
  fr: () => import(/* webpackChunkName: "locale-fr" */ './fr/default.json'),
  it: () => import(/* webpackChunkName: "locale-it" */ './it/default.json'),
  ja: () => import(/* webpackChunkName: "locale-ja" */ './ja/default.json'),
  pt: () => import(/* webpackChunkName: "locale-pt" */ './pt/default.json'),
  'zh-cn': () => import(/* webpackChunkName: "locale-zh-cn" */ './zh-cn/default.json'),
  'zh-tw': () => import(/* webpackChunkName: "locale-zh-tw" */ './zh-tw/default.json'),
};

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'es', 'pt', 'de', 'it', 'ja', 'zh-cn', 'zh-tw'];

// Shopify sends region-qualified locales (e.g. "zh-CN", "zh-TW", "pt-BR", "fr-CA").
// Exact match first (so zh-cn / zh-tw survive), then fall back to the base language.
export const normalizeLanguage = (value) => {
  if (!value) return null;
  const lower = value.toLowerCase();
  if (SUPPORTED_LANGUAGES.includes(lower)) return lower;
  const base = lower.split('-')[0];
  return SUPPORTED_LANGUAGES.includes(base) ? base : null;
};

const queryParams = new URLSearchParams(window.location?.search);
const shopifyLocale = queryParams.get('locale');
const savedAppLanguage = localStorage.getItem('app_language');

const hasUserOverride =
  savedAppLanguage && savedAppLanguage.trim() !== '';

const initialLanguage = hasUserOverride
  ? normalizeLanguage(savedAppLanguage) || 'en'
  : normalizeLanguage(shopifyLocale) || 'en';

i18next
  .use(initReactI18next)
  .init({
    resources: {},
    lng: initialLanguage,
    fallbackLng: 'en',
    // Keep codes lowercase so 'zh-cn' / 'zh-tw' match the bundle names above
    // instead of being rewritten to 'zh-CN' / 'zh-TW'.
    lowerCaseLng: true,
    supportedLngs: SUPPORTED_LANGUAGES,
    interpolation: {
      escapeValue: false,
    },
    react: {
      // Resources arrive via addResourceBundle below; bindI18nStore makes
      // useTranslation() re-render when a bundle lands, so any component that
      // mounted before the (fast, parallel-with-bootstrap) locale chunk
      // resolved picks up real strings instead of staying on raw keys.
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added',
    },
  });

export const loadLanguage = (lang) => {
  const normalized = normalizeLanguage(lang) || 'en';
  if (i18next.hasResourceBundle(normalized, 'translation')) {
    return Promise.resolve(normalized);
  }
  return localeLoaders[normalized]().then((mod) => {
    i18next.addResourceBundle(normalized, 'translation', mod.default, true, true);
    return normalized;
  });
};

// Kick off the active language (and the 'en' fallback when different)
// immediately — this downloads in parallel with app-bridge.js and the
// bootstrap API call, so it's resolved long before the shopInfo gate clears
// and the first translated UI mounts.
export const i18nReady = Promise.all([
  loadLanguage(initialLanguage),
  initialLanguage !== 'en' ? loadLanguage('en') : Promise.resolve('en'),
]);

export default i18next;
